import React, { useState, useEffect } from 'react';
import { X, MapPin, Star, Calendar, Home } from 'lucide-react';
import AccommodationCard from './AccommodationCard';
import api from '../services/api';
import modalsBg from '../assets/Modals.jpg';

const OwnerProfileModal = ({ isOpen, onClose, ownerId, onPropertyClick }) => {
  const [owner, setOwner] = useState(null);
  const [ownerListings, setOwnerListings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && ownerId) {
      setShowAllReviews(false);
      fetchOwnerData();
    }
  }, [isOpen, ownerId]);

  const fetchOwnerData = async () => {
    try {
      setLoading(true);
      const [ownerData, listingsData, reviewsData] = await Promise.all([
        api.getUserById(ownerId),
        api.getOwnerListings(ownerId),
        api.getOwnerReviews(ownerId)
      ]);
      setOwner(ownerData);
      setOwnerListings(listingsData);
      setReviews(reviewsData);
    } catch (error) {
      console.error('Failed to fetch owner data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;
  const visibleReviews = showAllReviews ? reviews : reviews.slice(0, 2);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl max-w-4xl w-full h-[92vh] sm:h-auto max-h-[92vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all duration-200 z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Header */}
          <div className="relative px-5 sm:px-8 py-3 sm:py-4 overflow-hidden" style={{ backgroundImage: `url(${modalsBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0 bg-gradient-to-r from-blue-950/60 via-blue-900/30 to-transparent"></div>
            <div className="relative flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
              {loading ? (
                <div className="text-white/80 text-sm">Loading owner information...</div>
              ) : (
                <>
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden ring-4 ring-white/80 shadow-xl flex-shrink-0 bg-white">
                    <img
                      src={owner?.picture || 'https://via.placeholder.com/96?text=User'}
                      alt={owner?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="text-white text-center sm:text-left">
                    <h2 className="text-base sm:text-lg font-bold mb-0.5">{owner?.name}</h2>
                    <div className="flex flex-wrap items-center justify-center sm:justify-start gap-1.5">
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/15 text-blue-50 text-[10px] sm:text-xs font-medium backdrop-blur-sm">
                        <Calendar className="w-3 h-3" /> Member since {owner?.created_at ? new Date(owner.created_at).getFullYear() : '2026'}
                      </span>
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/15 text-blue-50 text-[10px] sm:text-xs font-medium backdrop-blur-sm">
                        <Home className="w-3 h-3" /> {ownerListings.length} listings
                      </span>
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/15 text-blue-50 text-[10px] sm:text-xs font-medium backdrop-blur-sm">
                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" /> {averageRating} ({reviews.length} reviews)
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading owner information...</div>
          ) : (
            <>
              {/* Bio */}
              {owner?.bio && (
                <div className="mb-8">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">About</h3>
                  <p className="text-gray-700 leading-relaxed">{owner.bio}</p>
                </div>
              )}

              {/* Contact Info */}
              {(owner?.phone || owner?.location) && (
                <div className="mb-8">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Contact Information</h3>
                  <div className="space-y-2 text-gray-700">
                    {owner.phone && (
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">Phone:</span>
                        <span>{owner.phone}</span>
                      </div>
                    )}
                    {owner.location && (
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">Location:</span>
                        <MapPin className="w-4 h-4" />
                        <span>{owner.location}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Reviews */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  Reviews ({reviews.length})
                </h3>
                {reviews.length > 0 ? (
                  <div className="space-y-3">
                    {visibleReviews.map((review, idx) => (
                      <div key={idx} className="bg-white border border-gray-100 shadow-sm rounded-2xl p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <img
                              src={review.reviewer_picture || 'https://via.placeholder.com/40?text=U'}
                              alt={review.reviewer_name}
                              className="w-10 h-10 rounded-full object-cover shrink-0"
                            />
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900 truncate">{review.reviewer_name}</p>
                              <p className="text-xs text-gray-500">
                                {new Date(review.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-4 h-4 ${
                                  i < review.rating
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'fill-gray-200 text-gray-200'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <p className="text-gray-700 text-sm leading-relaxed">{review.review}</p>
                      </div>
                    ))}
                    {reviews.length > 2 && (
                      <button
                        onClick={() => setShowAllReviews((prev) => !prev)}
                        className="text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        {showAllReviews ? 'See less reviews' : 'See more reviews'}
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No reviews yet</p>
                )}
              </div>

              {/* Listings */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  All Listings ({ownerListings.length})
                </h3>
                {ownerListings.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {ownerListings.map((listing) => (
                      <AccommodationCard
                        key={listing.id}
                        accommodation={listing}
                        onClick={() => {
                          onPropertyClick(listing);
                          onClose();
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No active listings</p>
                )}
              </div>
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnerProfileModal;
